/**
 * MeetingPrepPersonCard - Displays a single attendee in the meeting prep people section.
 *
 * Shows avatar, name, RSVP status, title/company, "New" badge, and research summary.
 * Research data is passed in as a prop from the parent's batched fetch.
 */

import { useCallback } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { useOpenSlideout } from '@/lib/crm/hooks/useOpenSlideout';
import type { Research } from '@/lib/crm/record/research/useResearch';
import { getObject } from '@/lib/crm/store/crmObjectStore';

import {
  type MeetingPrepPerson,
  RSVP_DISPLAY,
} from '../utils/meetingPrepTypes';

interface MeetingPrepPersonCardProps {
  person: MeetingPrepPerson;
  rsvpStatus: string | undefined;
  research?: Research;
}

export function MeetingPrepPersonCard({
  person,
  rsvpStatus,
  research,
}: MeetingPrepPersonCardProps) {
  const openSlideout = useOpenSlideout();

  // getObject is an imperative store read (no subscription); call on every
  // render so cached store updates are reflected without memoizing stale data.
  const contactObject = person.entity_id
    ? getObject(person.entity_id)
    : undefined;

  const handleViewResearch = useCallback(() => {
    if (person.research_entity_id) {
      openSlideout(person.research_entity_id);
    } else if (person.entity_id) {
      openSlideout(person.entity_id);
    }
  }, [person.entity_id, person.research_entity_id, openSlideout]);

  // Resolve RSVP config, falling back to needs_action for unknown statuses
  const rsvpConfig =
    rsvpStatus !== undefined
      ? (RSVP_DISPLAY[rsvpStatus] ?? RSVP_DISPLAY['needs_action'])
      : null;

  // Build subtitle from title and company_name
  const subtitleParts = [person.title, person.company_name].filter(Boolean);
  const subtitle = subtitleParts.join(' · ');

  const showResearch = !!person.research_entity_id;
  const researchInProgress =
    !research ||
    research.status === 'queued' ||
    research.status === 'in_progress';

  return (
    <div className='space-y-2 py-3 border-b border-[var(--color-border-secondary)] last:border-b-0'>
      {/* Name + badges row */}
      <div className='flex items-center gap-2 flex-wrap'>
        {contactObject ? (
          <EntityBadgeById
            entityId={contactObject.entity_id}
            orgId={contactObject.org_id}
            variant='xs'
          />
        ) : (
          <span className='text-[13px] font-semibold text-[var(--color-text-primary)]'>
            {person.name ?? person.email}
          </span>
        )}
        {person.is_new && (
          <Badge size='sm' color='blue'>
            New
          </Badge>
        )}
        {rsvpConfig && (
          <span className='inline-flex items-center gap-1 text-xs text-[var(--color-text-secondary)]'>
            <span
              className={`w-1.5 h-1.5 rounded-full ${rsvpConfig.dotClass}`}
            />
            {rsvpConfig.label}
          </span>
        )}
      </div>

      {/* Subtitle row */}
      {subtitle && (
        <p className='text-xs text-[var(--color-text-secondary)]'>{subtitle}</p>
      )}

      {/* Research block */}
      {showResearch && (
        <>
          {researchInProgress && (
            <div className='animate-pulse space-y-1.5'>
              <div className='h-3 bg-[var(--color-bg-tertiary)] rounded w-full' />
              <div className='h-3 bg-[var(--color-bg-tertiary)] rounded w-3/4' />
            </div>
          )}
          {!researchInProgress && research?.status === 'complete' && (
            <div className='rounded-lg bg-[var(--color-bg-secondary)] p-3'>
              {research.summary ? (
                <p className='text-xs text-[var(--color-text-secondary)] line-clamp-3'>
                  {research.summary}
                </p>
              ) : (
                <p className='text-xs text-[var(--color-text-tertiary)]'>
                  Research complete
                </p>
              )}
              {person.research_entity_id && (
                <Button
                  color='link-color'
                  size='sm'
                  onClick={handleViewResearch}
                  className='mt-1'
                >
                  View full research
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
